import { useQuery } from '@tanstack/react-query';
// api
import { dashboardApi } from '@/domains/traffic-monitoring/apis/dashboard';
// constant
import {
  DASHBOARD_CONSTANTS,
  VEHICLE_VOLUME_BUCKETS,
} from '@/domains/traffic-monitoring/constants/dashboard.constants';
import type {
  TrafficVolumeByVehicleItem,
  TrafficVolumeByVehicleParams,
} from '../types/dashboard.types';

export interface VehicleVolumeBucket {
  title: string;
  total: number;
  changePct: number | null;
  typeArrow: 'up' | 'down';
}

// Signed day-over-day change, 1 dp. Mirrors the backend `_change_pct`:
// 0.0 when both are 0; null when there is no baseline (yesterday 0, today > 0).
const changePct = (today: number, yesterday: number): number | null => {
  if (yesterday > 0) return Math.round(((today - yesterday) / yesterday) * 1000) / 10;
  if (today === 0) return 0;
  return null;
};

const toBuckets = (items: TrafficVolumeByVehicleItem[] | undefined): VehicleVolumeBucket[] => {
  const byCode = new Map((items ?? []).map((it) => [it.object_class_code, it]));
  return VEHICLE_VOLUME_BUCKETS.map(({ title, classes }) => {
    let total = 0;
    let yesterday = 0;
    classes.forEach((code) => {
      const it = byCode.get(code);
      if (it) {
        total += it.count;
        yesterday += it.yesterday_count;
      }
    });
    const pct = changePct(total, yesterday);
    // up (red danger) = more traffic vs. yesterday; down (green) = less.
    const typeArrow: 'up' | 'down' = pct === null || pct >= 0 ? 'up' : 'down';
    return { title, total, changePct: pct, typeArrow };
  });
};

export const useTrafficVolumeByVehicle = (params: TrafficVolumeByVehicleParams = {}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['trafficVolumeByVehicle', params],
    queryFn: () => dashboardApi.getTrafficVolumeByVehicle(params),
    staleTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.STALE_TIME_MS,
    gcTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.CACHE_TIME_MS,
    // Keep the dashboard cadence; hold the previous values while refetching.
    refetchInterval: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.REFETCH_INTERVAL_MS,
    placeholderData: (prev) => prev,
  });

  return {
    buckets: toBuckets(data?.data?.group_by_vehicle),
    totalVolume: data?.data?.total_volume ?? 0,
    totalChangePct: data?.data?.total_change_pct ?? null,
    isLoading,
    isError,
  };
};
